/* eslint-disable react/prop-types */
import Select, { components, OptionProps } from 'react-select';
import { SelectorPanelOption, RowFlex, ColFlex } from './style';
import { Id } from 'react-toastify';
import { appToast } from '../../Basic/CustomToast';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { changePanel, setPanelLoading } from 'store/slices/RoofDataSlice/roofDataSlice';
import useScreenDims from 'hooks/useScreenDims';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import { setThreeDState } from 'store/slices/ToolSlice';
import ConfirmationModal from 'components/Basic/Modal/Confirmation';
import Button from 'components/Basic/Button';
import { changePanelAndRetianLayout, addPanelLayout } from './api';
import { useMutation } from 'react-query';
import { AxiosError } from 'axios';
import { errorResponseHandler } from 'store/api/AxiosError';
import { selectIsRecomputing } from 'store/slices/DsmShadingSlice/selectors';
import { FacetEditShadingStatus } from 'api/editDsm';
import useStartShadingRecalc from 'hooks/useStartShadingRecalc';

type PanelDetailsSelector = (PanelDetails & { label: string, value: string });

type Props = {
	panelDetails: PanelDetails[],
	panelFinancingInfo: panelFinancingInfo[],
	panelKey: string;
	panelsKeysForWhichDesignIsAvailable: string[]
}

function PanelCard(props: OptionProps<PanelDetailsSelector>) {
	const { name, manufacturer, wattage, financialKeys } = props.data;
	return (
		<components.Option {...props} >
			<SelectorPanelOption>
				<RowFlex>
					<ColFlex>
						<h3>{manufacturer} {financialKeys && <span><b>{financialKeys}</b></span>}</h3>
						<span>{name}</span>
					</ColFlex>
					<h4>{wattage * 1000}w </h4>
				</RowFlex>
			</SelectorPanelOption>
		</components.Option >
	);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SingleValue = (props: any) => {
	const { label, financialKeys } = props.getValue()[0];

	return (
		<components.SingleValue {...props}>
			<span>{label}</span> {financialKeys && <span><b>{financialKeys}</b></span>}
		</components.SingleValue>
	);
};

export default function PanelSelector(props: Props) {
	const { panelDetails, panelKey, panelFinancingInfo, panelsKeysForWhichDesignIsAvailable } = props;
	const dispatch = useDispatch<AppDispatch>();
	const { width } = useScreenDims();
	const queriesRunningInBackGround = useQueriesRunningInBackGround();
	const { configuringNewPanel, error, data: roofData } = useSelector((state: RootState) => state.roofData);
	const { threeDModelEnabled, hideSatelliteOverlay } = useSelector((state: RootState) => state.toolNewPostions.data);
	const { editModeEnabled, deletedRoofIndexes, drawModeEnabled, roofIndexes, deleteObstructionsMode, newlyCreatedFacets, enableEditObs } =
		useSelector((state: RootState) => state.toolNewPostions.data);
	const { activePanelMode } = useSelector((state: RootState) => state.panelSlice.data);
	const { inputFieldsEdited } = useSelector((state: RootState) => state.manualToolModal.data);
	const hasUserMadeModificaiton = !!deletedRoofIndexes?.length || !!Object.keys(newlyCreatedFacets)?.length || !!roofIndexes?.length;
	const disablePanelSwitch = editModeEnabled
		&& (hasUserMadeModificaiton || activePanelMode || drawModeEnabled || deleteObstructionsMode || enableEditObs);
	const { aiToolPanelKeys } = useSelector((state: RootState) => state.QueryParams.data);
	const previouseToastId = useRef<Id>();
	const isRecomputing = useSelector(selectIsRecomputing);
	const [confirmModal, setConfirmModal] = useState(false);
	const startShadingRecalc = useStartShadingRecalc();

	const panelKeysWithLabel = useMemo(() => panelDetails
		.filter(p => !aiToolPanelKeys?.length || aiToolPanelKeys?.includes(p.key))
		.map(p => {
			let financialKeys = '';
			if (panelFinancingInfo?.length) {
				const foundKey = panelFinancingInfo.find((val) => val.panelKey === p.key);
				if (foundKey)
					financialKeys = '(' + foundKey.financialKeys.join('-') + ')';
			}
			return {
				...p,
				name: p.nickName || p.name,
				label: p.nickName || p.name || `${p.manufacturer}-${p.name}-${p.wattage}`,
				financialKeys,
				value: p.key,
			};
		}) as unknown as PanelDetailsSelector[], [panelDetails, panelFinancingInfo, aiToolPanelKeys]);
	const [selectedPanelValue, setSelectedPanelValue] = useState<PanelDetailsSelector | undefined>(
		() => panelKeysWithLabel.find(p => p.value.toUpperCase() === panelKey.toUpperCase())
	);

	const confirmedPanelRef = useRef<PanelDetailsSelector | undefined>(
		panelKeysWithLabel.find(p => p.value.toUpperCase() === panelKey.toUpperCase())
	);

	const panelRef = useRef<string>(panelKey);

	const disable3D = () => {
		dispatch(setThreeDState({ enabled: false }));
	};

	const showErrorToastOnChnagePanel = useCallback(function () {
		if (!error || previouseToastId?.current) return;
		const errorCode = error.error;
		let msg = error.message;
		if (errorCode === 'E_INVALID_PANEL')
			msg = `${panelRef.current} panel is not present`;
		previouseToastId.current = appToast.error(
			msg,
			{
				onClose: () => {
					previouseToastId.current = undefined;
				},
			}
		);
	}, [error]);

	useEffect(() => {
		showErrorToastOnChnagePanel();
	}, [error, showErrorToastOnChnagePanel]);

	useEffect(() => {
		const current = panelKeysWithLabel.find(p => p.value.toUpperCase() === panelKey.toUpperCase());
		panelRef.current = panelKey;
		confirmedPanelRef.current = current;
		setSelectedPanelValue(current);
		setConfirmModal(false);
	}, [panelKey, panelKeysWithLabel]);

	const handleCancel = () => {
		panelRef.current = confirmedPanelRef.current?.value ?? panelKey;
		setSelectedPanelValue(confirmedPanelRef.current);
		setConfirmModal(false);
	};

	const handleRedesignLayout = (redesign: boolean) => {
		const newValue = panelKeysWithLabel.find(p => p.value === panelRef.current);
		confirmedPanelRef.current = newValue;
		setSelectedPanelValue(newValue);
		setConfirmModal(false);

		addPanelLayoutMutation.mutate({
			uuid: roofData.uuid,
			panelKey: panelRef.current,
			designType: roofData.designType,
			resetPanelKey: redesign
		});

	};

	const addPanelLayoutMutation = useMutation<
		FacetEditShadingStatus | undefined,
		AxiosError,
		{ uuid: string; panelKey: string; designType: DesignType, resetPanelKey: boolean }
	>(
		['add-panel-layout'],
		async ({ uuid, panelKey, resetPanelKey }) => {
			return await addPanelLayout({ uuid, panelKey, resetPanelKey });
		},
		{
			onMutate: () => {
				dispatch(setPanelLoading(true));
			},
			onSuccess: (shadingStatus, variables) => {
				const newValue = panelKeysWithLabel.find(p => p.value === variables.panelKey);
				confirmedPanelRef.current = newValue;
				setSelectedPanelValue(newValue);
				dispatch(changePanel({
					uuid: variables.uuid,
					panelKey: variables.panelKey,
					designType: variables.designType,
				}));
				startShadingRecalc(shadingStatus);
			},
			onError: (err) => {
				dispatch(setPanelLoading(false));
				const rollback = panelKeysWithLabel.find(p => p.value.toUpperCase() === panelKey.toUpperCase());
				panelRef.current = panelKey;
				confirmedPanelRef.current = rollback;
				setSelectedPanelValue(rollback);
				const { message } = errorResponseHandler(err);
				appToast.error(message || 'Something went wrong while changing panel.', {
					toastId: 'change-panel-error',
				});
			},
		}
	);

	const preserveLayoutMutation = useMutation<
		FacetEditShadingStatus | undefined,
		Error,
		{ uuid: string; panelKey: string; designType: DesignType }
	>(
		['preserve-layout'],
		async ({ uuid, panelKey }) => {
			// Uses axiosInstance + Authorization header inside changePanelAndRetianLayout
			return await changePanelAndRetianLayout(uuid, panelKey);
		},
		{
			onMutate: () => {
				dispatch(setPanelLoading(true));
			},
			onSuccess: (shadingStatus, variables) => {
				// dispatch only when everything succeeded
				const newValue = panelKeysWithLabel.find(p => p.value === variables.panelKey);
				confirmedPanelRef.current = newValue;
				setSelectedPanelValue(newValue);
				dispatch(changePanel({
					uuid: variables.uuid,
					panelKey: variables.panelKey,
					designType: variables.designType,
				}));
				startShadingRecalc(shadingStatus);
			},
			onError: (err) => {
				dispatch(setPanelLoading(false));
				const rollback = panelKeysWithLabel.find(p => p.value.toUpperCase() === panelKey.toUpperCase());
				panelRef.current = panelKey;
				confirmedPanelRef.current = rollback;
				setSelectedPanelValue(rollback);
				appToast.error(err.message || 'Something went wrong while preserving layout.', {
					toastId: 'retail-panel-error',
				});
			},
		}
	);

	const handlePreserveLayout = () => {
		const newValue = panelKeysWithLabel.find(p => p.value === panelRef.current);
		confirmedPanelRef.current = newValue;
		setSelectedPanelValue(newValue);
		setConfirmModal(false);
		preserveLayoutMutation.mutate({
			uuid: roofData.uuid,
			panelKey: panelRef.current,
			designType: roofData.designType,
		});
	};

	return (
		<>
			<Select
				className="react-select-wrap"
				classNamePrefix="react-change-panel"
				options={panelKeysWithLabel}
				components={{ Option: PanelCard, SingleValue }}
				menuPlacement={width > 935 ? 'bottom' : 'top'}
				styles={{
					control: (base, props) => ({
						...base,
						cursor: props.isDisabled ? 'not-allowed' : base.cursor,
						opacity: props.isDisabled ? .6 : 1
					}),
					menu: base => ({
						...base,
						zIndex: 2
					}),
					menuList: base => ({
						...base,
						maxHeight: '200px',
						width: '100%',
					}),
					option: (base, props) => {
						let backgroundColor = base.backgroundColor;
						if (props.isFocused)
							backgroundColor = 'var(--selectDropdownHoverColor)';
						if (props.isSelected)
							backgroundColor = 'var(--selectedBgColor)';
						return {
							margin: 0,
							padding: 0,
							backgroundColor
						};
					},
					loadingIndicator: base => ({
						...base,
						color: 'var(--textColor)',
					}),
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					indicatorSeparator: _base => ({

					}),
					container: base => ({
						...base,
						marginTop: '0.3em',
						width: '100%'
					})
				}}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				onChange={(data: any) => {
					if (panelRef.current === data.value) return;
					panelRef.current = data.value as string;
					const newValue = panelKeysWithLabel.find(p => p.value === data.value);
					setSelectedPanelValue(newValue);
					if (threeDModelEnabled)
						disable3D();
					setConfirmModal(true);
				}}
				isLoading={configuringNewPanel}
				value={selectedPanelValue}
				isDisabled={configuringNewPanel || queriesRunningInBackGround || disablePanelSwitch || inputFieldsEdited
					|| isRecomputing || hideSatelliteOverlay}
				isSearchable={false}
			/>

			<ConfirmationModal
				open={confirmModal}
				onConfirm={() => {console.log('');}}
				onCancel={handleCancel}
				heading="Change Panel"
				message="How would you like to apply the new panel?"
				renderFooter={() => (
					<>
						<Button
							onClick={handlePreserveLayout}
							autoFocus
							style={{ borderColor: 'var(--primary)' }}
							key={1}
						>
							Preserve Layout
						</Button>
						{
							panelsKeysForWhichDesignIsAvailable.includes(selectedPanelValue?.value || '') &&
							<Button className='btn-no' onClick={() => handleRedesignLayout(false)} key={2}>
								Existing Layout
							</Button>
						}
						<Button className='btn-no' onClick={() => handleRedesignLayout(true)} key={3}>
							Reset Layout
						</Button>						

					</>
				)}
			/>
		</>
	);
}
